import React, { useState } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { getMonthlyDailyCountReport } from "../../core/api/reportService";
import { getProcedures } from "../../core/api/procedureService";
import StickyScrollTable from "../components/StickyScrollTable";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";
import { REPORT_CATEGORIES } from "../../core/constants/reportCategories";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const pad = (n) => String(n).padStart(2, "0");
const displayDate = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };

// Count columns in display order; New OP and Total bracket the procedure categories.
const COLUMNS = [
  { key: "newOP", label: "New OP" },
  ...REPORT_CATEGORIES.map(({ key, label }) => ({ key, label })),
  { key: "totalPatients", label: "Total No. of Patients" },
];

// GET /api/reports/monthly-daily-count may omit days with no activity — list every day of the
// month, with zeros, so the sheet is a full month. Field names are matched case-insensitively
// so a PascalCase response ("ImplantCases") still lands in the right column.
const fillMonth = (dates, apiRows) => {
  const lower = (r) => Object.fromEntries(Object.entries(r || {}).map(([k, v]) => [k.toLowerCase(), v]));
  const byDate = new Map(apiRows.map(lower).map((r) => [String(r.date || "").split("T")[0], r]));
  return dates.map((date) => {
    const r = byDate.get(date) || {};
    return { date, ...Object.fromEntries(COLUMNS.map((c) => [c.key, Number(r[c.key.toLowerCase()] || 0)])) };
  });
};

const MonthlyCountReport = () => {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState(null);
  // Active procedures with no Report Category: the backend can't count their invoices in any column.
  const [uncategorized, setUncategorized] = useState([]);
  const [loading, setLoading] = useState(false);

  const runReport = async () => {
    if (!month) return;
    const [year, m] = month.split("-").map(Number);
    const dates = Array.from({ length: new Date(year, m, 0).getDate() }, (_, i) => `${year}-${pad(m)}-${pad(i + 1)}`);
    setLoading(true);
    try {
      const [res, procRes] = await Promise.all([
        getMonthlyDailyCountReport({ year, month: m }),
        getProcedures().catch(() => ({ data: [] })),
      ]);
      setRows(fillMonth(dates, res.data || []));
      setUncategorized(
        (procRes.data || [])
          .filter((p) => (p.status || "Active") === "Active" && !p.reportCategory)
          .map((p) => p.procedureName)
          .sort((a, b) => (a || "").localeCompare(b || ""))
      );
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load the monthly report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const totals = Object.fromEntries(COLUMNS.map((c) => [c.key, (rows || []).reduce((s, r) => s + r[c.key], 0)]));

  const handleExport = () => {
    const headers = ["Date", ...COLUMNS.map((c) => c.label)];
    const dataRows = rows.map((r) => [displayDate(r.date), ...COLUMNS.map((c) => r[c.key])]);
    dataRows.push(["TOTAL", ...COLUMNS.map((c) => totals[c.key])]);
    exportToCsv(`Monthly-Report-${month}.csv`, headers, dataRows);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Monthly Report</h4><h6>Day-wise patient count by treatment</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">Month</label>
                <input type="month" className="form-control form-control-sm" value={month} onChange={(e) => setMonth(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading || !month}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows || rows.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && uncategorized.length > 0 && (
          <div className="alert alert-warning py-2 small">
            <strong>{uncategorized.length} procedure{uncategorized.length > 1 ? "s have" : " has"} no Report Category</strong>,
            so invoices for {uncategorized.length > 1 ? "them aren't" : "it aren't"} counted in any treatment column:{" "}
            {uncategorized.join(", ")}. Set a category in <Link to="/procedures">Procedures</Link>, then search again.
          </div>
        )}

        {rows && (
          <div className="card">
            <div className="card-body p-0">
              <StickyScrollTable>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      {COLUMNS.map((c) => <th key={c.key} className="text-center">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.date}>
                        <td className="text-nowrap">{displayDate(r.date)}</td>
                        {COLUMNS.map((c) => (
                          <td key={c.key} className={`text-center${c.key === "totalPatients" ? " fw-semibold" : ""}`}>
                            {r[c.key] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light fw-bold">
                      <td>TOTAL</td>
                      {COLUMNS.map((c) => <td key={c.key} className="text-center">{totals[c.key]}</td>)}
                    </tr>
                  </tfoot>
                </table>
              </StickyScrollTable>
            </div>
          </div>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a month and click <strong>Search</strong> to view the report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyCountReport;
